import {
  type QueryClient,
  queryOptions,
  skipToken,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect } from "react";

import { errorToast } from "@/components/ui/toast.tsx";
import { useSocket } from "@/contexts/SocketContext.tsx";
import { HANDED_OFF_CONFIRMATION, STILL_WAITING_CONFIRMATION } from "@/shared/taskRowCopy.ts";
import { taskErrorMessage } from "@/shared/userTaskErrors.ts";

import { announce } from "@/lib/announce.ts";
import { api } from "@/lib/http.ts";
import type {
  AddProjectTaskReq,
  AddTaskRuleReq,
  AddTaskRuleResp,
  DraftTaskRuleReq,
  DraftTaskRuleResp,
  MuteSenderReq,
  MuteSenderResp,
  OkResp,
  UnmuteSenderReq,
  UserTaskMutedSendersResp,
  UserTaskRulesResp,
  AnswerLabelProposalReq,
  AnswerSilenceReq,
  AnswerTriageReq,
  CheckTaskToolResp,
  HandoffReq,
  SetContextReq,
  SetUserTaskMirrorReq,
  SetUserTaskMirrorResp,
  SetUserTaskStatusReq,
  TaskThreadResp,
  UserTaskMirrorResp,
  UserTaskProject,
  DismissalReasonReq,
  SimpleTaskCategory,
  SimpleTasksResp,
  UserTasksResp,
} from "@/types/userTasks.ts";
import type { UserTaskAnswer } from "@/types/userTaskLinks.ts";
import { laterAt, shownStatus, snoozeUntil } from "@/shared/userTaskActions.ts";

export { laterAt, shownStatus, snoozeUntil };

/**
 * The user's own task list. Its own key root: `["tasks"]` belongs to the
 * per-thread task records, whose socket invalidations must not refetch this.
 */
export const userTaskQueries = {
  all: () => ["userTasks"] as const,
  list: () =>
    queryOptions({
      queryKey: userTaskQueries.all(),
      queryFn: async ({ signal }) =>
        (await api.get<UserTasksResp>("/api/a/user-tasks", { signal })).data,
    }),
  /**
   * One category of the simpler list, grouped and sorted by the server. Under
   * `all()`, so every task write refetches it as it does the list.
   */
  simple: (category: SimpleTaskCategory) =>
    queryOptions({
      queryKey: [...userTaskQueries.all(), "simple", category] as const,
      queryFn: async ({ signal }) =>
        (
          await api.get<SimpleTasksResp>("/api/a/user-tasks/simple", {
            params: { category },
            signal,
          })
        ).data,
    }),
  /** The user's own task tools and whether the list is mirrored to them. */
  mirror: () =>
    queryOptions({
      queryKey: [...userTaskQueries.all(), "mirror"] as const,
      queryFn: async ({ signal }) =>
        (await api.get<UserTaskMirrorResp>("/api/a/user-tasks/mirror", { signal })).data,
    }),
  /** The senders the user told okthx never to read for tasks. */
  mutedSenders: () =>
    queryOptions({
      queryKey: [...userTaskQueries.all(), "mutedSenders"] as const,
      queryFn: async ({ signal }) =>
        (await api.get<UserTaskMutedSendersResp>("/api/a/user-tasks/muted-senders", { signal }))
          .data,
    }),
  /** The user's rules about what is a task. */
  rules: () =>
    queryOptions({
      queryKey: [...userTaskQueries.all(), "rules"] as const,
      queryFn: async ({ signal }) =>
        (await api.get<UserTaskRulesResp>("/api/a/user-tasks/rules", { signal })).data,
    }),
  /** A label's page. Under the list's key root, so every list change refetches it. */
  project: (labelId: string) =>
    queryOptions({
      queryKey: [...userTaskQueries.all(), "project", labelId] as const,
      queryFn: async ({ signal }) =>
        (await api.get<UserTaskProject>(`/api/a/user-tasks/labels/${labelId}`, { signal })).data,
    }),
  /** One thread's tasks: the work okthx does there, and the tasks found in it. */
  thread: (threadId: number | undefined) =>
    queryOptions({
      queryKey: [...userTaskQueries.all(), "thread", threadId] as const,
      queryFn: threadId
        ? async ({ signal }) =>
            (await api.get<UserTasksResp>(`/api/a/threads/${threadId}/user-tasks`, { signal })).data
        : skipToken,
    }),
};

/**
 * Whether a loaded task is worked in this thread. Reads every cached entry
 * under the list's root, but only the ones that hold tasks: the mirror's has
 * another shape.
 */
export function cachedTaskWorksIn(queryClient: QueryClient, threadId: number): boolean {
  return queryClient
    .getQueriesData<unknown>({ queryKey: userTaskQueries.all() })
    .some(([, data]) => cachedTasks(data).some((task) => task.threadId === threadId));
}

/**
 * The tasks a cached list entry holds, in either shape: the list's own
 * `tasks`, or the simpler list's `groups[].tasks`. Anything else holds none.
 */
function cachedTasks(data: unknown): { threadId?: number | null }[] {
  if (!data || typeof data !== "object") return [];
  if ("tasks" in data && Array.isArray(data.tasks)) return data.tasks;
  if ("groups" in data && Array.isArray(data.groups)) {
    return data.groups.flatMap((group: unknown) =>
      group && typeof group === "object" && "tasks" in group && Array.isArray(group.tasks)
        ? group.tasks
        : [],
    );
  }
  return [];
}

/** Whether a thread change touches what a task's row shows of its thread. */
export function threadStatusChanged(diff: {
  activityState?: unknown;
  isPaused?: unknown;
  status?: unknown;
}): boolean {
  return (
    diff.activityState !== undefined || diff.isPaused !== undefined || diff.status !== undefined
  );
}

/**
 * Keeps every list query current: okthx opening or closing a task in a thread,
 * or detection finding one, refetches the Tasks tab and the thread's task view.
 */
export const useUserTasksSocket = () => {
  const { socket } = useSocket();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!socket) return;
    const onUserTasksMutated = () => {
      void queryClient.invalidateQueries({ queryKey: userTaskQueries.all() });
    };
    // A work thread that starts or stops waiting on the user changes whether
    // its task needs the user's input. Only threads a loaded task is worked
    // in, so ordinary chats cost no refetch.
    const onThreadMutated = ({
      threadId,
      diff,
    }: {
      threadId: number;
      diff?: { activityState?: unknown; isPaused?: unknown; status?: unknown };
    }) => {
      // The three a task's thread status is read from: running or waiting,
      // paused, and finished or archived.
      if (!diff || !threadStatusChanged(diff)) return;
      if (cachedTaskWorksIn(queryClient, threadId)) onUserTasksMutated();
    };
    // Socket.IO recovery is bounded: a socket back after a longer gap missed
    // events, so read the list again, as `useTasksSocket` does.
    onUserTasksMutated();
    socket.on("userTasks:mutated", onUserTasksMutated);
    socket.on("thread:mutated", onThreadMutated);
    return () => {
      socket.off("userTasks:mutated", onUserTasksMutated);
      socket.off("thread:mutated", onThreadMutated);
    };
  }, [socket, queryClient]);
};

/**
 * How a failed task write reaches the user, as one choice rather than two
 * independent settings.
 *
 * Either this wrapper raises the toast from `failure`, or the caller reports it
 * and says so. A union, not two optional fields, because the two combined would
 * say the same failure twice — a caller toasting from its own handler while a
 * `failure` message was also set. Making that unrepresentable is the point.
 *
 * `stale` marks a write that carried the state its control was showing, where a
 * conflict means the task moved underneath it rather than that the write was
 * wrong — see `taskErrorMessage`.
 */
type TaskReporting<TVars> =
  | { failure: string; stale?: boolean; reportedByCaller?: never; onError?: never }
  | {
      reportedByCaller: true;
      onError?: (error: Error, vars: TVars) => void;
      failure?: never;
      stale?: never;
    };

/**
 * A change to the list, which refetches it. `onSuccess` and the caller's own
 * `onError` run from the mutation itself, so they run even when the change
 * moved the row and unmounted the control that made it — callbacks passed to
 * `mutate` would not.
 *
 * A failure is reported by a toast from here rather than by the control that
 * made the write. The control is often a single glyph inside a flex row, and a
 * message rendered beside one stretched the row it sat in; the toast also
 * survives the row unmounting, which is exactly when a failed write is most
 * likely to have moved it.
 */
function useUserTaskMutation<TVars, TData>(
  mutationFn: (vars: TVars) => Promise<TData>,
  reporting: TaskReporting<TVars>,
  onSuccess?: (data: TData, vars: TVars) => void,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess,
    onError: (error: Error, vars: TVars) => {
      if (reporting.reportedByCaller) {
        reporting.onError?.(error, vars);
      } else {
        errorToast(taskErrorMessage(error, reporting.failure, { stale: reporting.stale ?? false }));
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: userTaskQueries.all() }),
  });
}

/** Why a task already dismissed was dismissed, given on the strip it left. */
export const useRecordDismissalReason = () =>
  useUserTaskMutation(
    async ({ taskId, ...body }: DismissalReasonReq & { taskId: string }) =>
      (await api.post<OkResp>(`/api/a/user-tasks/${taskId}/dismissal/reason`, body)).data,
    { failure: "Couldn't save the reason." },
  );

/** Undo a Dismiss: the task goes back as it was. */
export const useUndoDismissal = () =>
  useUserTaskMutation(
    async ({ taskId }: { taskId: string }) =>
      (await api.post<OkResp>(`/api/a/user-tasks/${taskId}/dismissal/undo`)).data,
    { failure: "Couldn't undo that." },
  );

/** Keep, Dismiss or Same as… on a suggested task. */
export const useAnswerTriage = () =>
  useUserTaskMutation(
    async ({ taskId, ...body }: AnswerTriageReq & { taskId: string }) =>
      (await api.post(`/api/a/user-tasks/${taskId}/triage`, body)).data,
    { failure: "Couldn't update this task." },
  );

/** Done, Drop, Later or Reopen. */
export const useSetUserTaskStatus = () =>
  useUserTaskMutation(
    async ({ taskId, ...body }: SetUserTaskStatusReq & { taskId: string }) =>
      (await api.post(`/api/a/user-tasks/${taskId}/status`, body)).data,
    // Only the status write sends the status the row drew.
    { failure: "Couldn't update this task.", stale: true },
  );

/**
 * Got it or Still waiting, on the user's task that waits on someone who has
 * gone quiet. The check-in's third answer, Hand to Okthx, is `useHandToOkthx`.
 *
 * Still waiting answers the question and restarts the quiet clock, and every
 * part of that is invisible once the card's question goes — so it says so.
 * Fired from the mutation rather than the row: answering removes the question
 * that asked, and a callback passed to `mutate` would not survive it.
 */
export const useAnswerSilence = () =>
  useUserTaskMutation(
    async ({ taskId, ...body }: AnswerSilenceReq & { taskId: string }) =>
      (await api.post(`/api/a/user-tasks/${taskId}/silence`, body)).data,
    { failure: "Couldn't update this task." },
    // From the mutation rather than the row: the answer takes the check-in
    // off the card, which unmounts the button that asked. Only for Still
    // waiting — the other answer finishes the task, which is its own evidence.
    (_data, vars) => {
      if (vars.answer === "stillWaiting") announce(STILL_WAITING_CONFIRMATION);
    },
  );

/**
 * The route and body one answer from a brief's "Your tasks" line goes to.
 * "Later" means tomorrow, the Tasks tab's first choice. A move says the status
 * the brief showed the task in — open, or done for one okthx closed — so a
 * task that changed since the brief went out is a 409, not moved again.
 */
export function answerRequest(
  answer: UserTaskAnswer,
  now: Date = new Date(),
): { route: "triage" | "status" | "silence" | "handoff"; body: object } {
  switch (answer) {
    case "keep":
    case "dismiss":
      return { route: "triage", body: { answer } };
    case "gotIt":
    case "stillWaiting":
      return { route: "silence", body: { answer } };
    case "handOff":
      return { route: "handoff", body: {} };
    case "reopen":
      return { route: "status", body: { status: "open", fromStatus: "done" } };
    case "done":
      return { route: "status", body: { status: "done", fromStatus: "open" } };
    case "drop":
      return { route: "status", body: { status: "cancelled", fromStatus: "open" } };
    case "later":
      return {
        route: "status",
        body: { status: "later", snoozedUntilMs: laterAt(now), fromStatus: "open" },
      };
  }
}

/**
 * One answer from a brief's "Your tasks" line, sent to whichever route owns
 * it. A handoff answers with the conversation the task is now worked in.
 */
export const useAnswerUserTask = () =>
  useUserTaskMutation(
    async ({ taskId, answer }: { taskId: string; answer: UserTaskAnswer }) => {
      const { route, body } = answerRequest(answer);
      return (await api.post<{ threadId?: number }>(`/api/a/user-tasks/${taskId}/${route}`, body))
        .data;
    },
    { reportedByCaller: true },
  );

/** Hand to okthx: the task becomes okthx's, and answers with the conversation it is worked in. */
export const useHandToOkthx = (onHandedOff?: (handed: TaskThreadResp) => void) =>
  useUserTaskMutation(
    async ({ taskId, ...body }: HandoffReq & { taskId: string }) =>
      (await api.post<TaskThreadResp>(`/api/a/user-tasks/${taskId}/handoff`, body)).data,
    { failure: "Couldn't hand it to Okthx." },
    // Here rather than at each caller, so every way of handing a task over
    // says so: the row moving to another group is the confirmation, and a
    // reader who cannot see it moving gets nothing at all.
    (handed) => {
      announce(HANDED_OFF_CONFIRMATION);
      onHandedOff?.(handed);
    },
  );

/** Mute a sender or domain: never read for tasks again, and its tasks dropped. */
export const useMuteSenderRequest = (
  onMuted?: (muted: MuteSenderResp, req: MuteSenderReq) => void,
) =>
  useUserTaskMutation(
    async (body: MuteSenderReq) =>
      (await api.post<MuteSenderResp>("/api/a/user-tasks/muted-senders", body)).data,
    { failure: "Couldn't mute the sender." },
    onMuted,
  );

/**
 * Undo a mute, putting back the tasks it dropped when it says which.
 *
 * Takes its reporting because its two callers differ: the skips panel leaves it
 * to the generic toast, while `useMuteSender` names the sender it could not put
 * back and offers Retry.
 */
export const useUnmuteSender = (reporting: TaskReporting<UnmuteSenderReq>) =>
  useUserTaskMutation(
    async (body: UnmuteSenderReq) =>
      (await api.post<OkResp>("/api/a/user-tasks/muted-senders/unmute", body)).data,
    reporting,
  );

/**
 * The model's draft of a rule from a task and the user's words. Saves nothing,
 * so it does not go through `useUserTaskMutation` and would refetch the list
 * for no reason — but it still has to say when it fails, and in the words a
 * failed save uses: the panel shows one line for both.
 */
export const useDraftTaskRule = () =>
  useMutation({
    mutationFn: async ({ taskId, ...body }: DraftTaskRuleReq & { taskId: string }) =>
      (await api.post<DraftTaskRuleResp>(`/api/a/user-tasks/${taskId}/rule-draft`, body)).data,
    onError: (error: Error) => errorToast(taskErrorMessage(error, "Couldn't save the rule.")),
  });

/** Save a rule: new tasks are judged by it. */
export const useAddTaskRule = (onSaved?: (saved: AddTaskRuleResp) => void) =>
  useUserTaskMutation(
    async (body: AddTaskRuleReq) =>
      (await api.post<AddTaskRuleResp>("/api/a/user-tasks/rules", body)).data,
    { failure: "Couldn't save the rule." },
    onSaved,
  );

export const useDeleteTaskRule = () =>
  useUserTaskMutation(
    async ({ ruleId }: { ruleId: string }) =>
      (await api.delete<OkResp>(`/api/a/user-tasks/rules/${ruleId}`)).data,
    { failure: "Couldn't change it." },
  );

/** Ask about this: a conversation about the task, which the web opens. */
export const useAskAboutTask = (onAsked?: (asked: TaskThreadResp) => void) =>
  useUserTaskMutation(
    async ({ taskId }: { taskId: string }) =>
      (await api.post<TaskThreadResp>(`/api/a/user-tasks/${taskId}/ask`, {})).data,
    { failure: "Couldn't start a conversation about it." },
    onAsked,
  );

/** Take a task back from okthx. */
export const useTakeBack = () =>
  useUserTaskMutation(
    async ({ taskId }: { taskId: string }) =>
      (await api.post(`/api/a/user-tasks/${taskId}/take-back`, {})).data,
    { failure: "Couldn't take it back." },
  );

/** In progress: the reader is on it themselves, until they mark it done. */
export const useHoldTask = () =>
  useUserTaskMutation(
    async ({ taskId }: { taskId: string }) =>
      (await api.post(`/api/a/user-tasks/${taskId}/hold`, {})).data,
    { failure: "Couldn't mark it in progress." },
  );

/** Keep or Dismiss a label okthx proposes. */
export const useAnswerLabelProposal = () =>
  useUserTaskMutation(
    async ({ labelId, ...body }: AnswerLabelProposalReq & { labelId: string }) =>
      (await api.post(`/api/a/user-tasks/labels/${labelId}/proposal`, body)).data,
    { failure: "Couldn't answer this proposal." },
  );

/** Reopen a project okthx closed when its goal looked met. */
export const useReopenProject = () =>
  useUserTaskMutation(
    async ({ labelId }: { labelId: string }) =>
      (await api.post(`/api/a/user-tasks/labels/${labelId}/reopen`, {})).data,
    { failure: "Couldn't reopen this project." },
  );

/** The user corrects where a task stands. */
export const useSetTaskContext = () =>
  useUserTaskMutation(
    async ({ taskId, ...body }: SetContextReq & { taskId: string }) =>
      (await api.patch(`/api/a/user-tasks/${taskId}/context`, body)).data,
    { failure: "Couldn't save that." },
  );

/** The user corrects where a label stands. */
export const useSetLabelContext = () =>
  useUserTaskMutation(
    async ({ labelId, ...body }: SetContextReq & { labelId: string }) =>
      (await api.patch(`/api/a/user-tasks/labels/${labelId}/context`, body)).data,
    { failure: "Couldn't save that." },
  );

/** Add a missing part of a project's goal as a task under it. */
export const useAddProjectTask = () =>
  useUserTaskMutation(
    async ({ labelId, ...body }: AddProjectTaskReq & { labelId: string }) =>
      (await api.post<{ taskId: string }>(`/api/a/user-tasks/labels/${labelId}/tasks`, body)).data,
    { failure: "Couldn't add that task." },
  );

/** Switch mirroring to one of the user's own task tools on or off. */
export const useSetUserTaskMirror = (label: string) =>
  useUserTaskMutation(
    async ({ service, ...body }: SetUserTaskMirrorReq & { service: string }) =>
      (await api.post<SetUserTaskMirrorResp>(`/api/a/user-tasks/mirror/${service}`, body)).data,
    { failure: `Couldn't change syncing with ${label}.` },
  );

/**
 * The user asks okthx to sync their tool now, rather than waiting for the
 * hourly sync or the next brief.
 */
export const useCheckTaskTool = (label: string) =>
  useUserTaskMutation(
    async ({ service }: { service: string }) =>
      (await api.post<CheckTaskToolResp>(`/api/a/user-tasks/mirror/${service}/check`, {})).data,
    { failure: `Couldn't sync with ${label}.` },
  );
